import { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import RefreshIcon from '@mui/icons-material/Refresh';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { AdminRole, AuthSettings, AuthSettingsUpdate } from '../../types/admin';
import { authSettingsApi, rolesApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';

/** Giá trị select "không gán vai trò mặc định" — được ánh xạ sang null khi lưu. */
const NO_DEFAULT_ROLE = '__none__';

/** Ảnh chụp các trường trên form, dùng để so sánh phát hiện thay đổi chưa lưu (isDirty). */
interface FormSnapshot {
  googleEnabled: boolean;
  clientId: string;
  autoCreateUser: boolean;
  requireApproval: boolean;
  defaultRoleId: string;
  allowedDomains: string;
  webhookUrl: string;
}

/**
 * Cấu hình đăng nhập Google (Admin only). chat_webhook_url server luôn trả về
 * đã CHE (vd "https://chat.googleapis.com/••••••") — nếu Admin không sửa ô
 * này, ta gửi lại nguyên chuỗi che, backend hiểu là "không đổi" và giữ URL
 * thật. KHÔNG cố "làm sạch" hay diễn giải chuỗi che này.
 */
export function GoogleAuthSettings(): JSX.Element {
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Form state
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [clientId, setClientId] = useState('');
  const [autoCreateUser, setAutoCreateUser] = useState(false);
  const [requireApproval, setRequireApproval] = useState(false);
  const [defaultRoleId, setDefaultRoleId] = useState<string>(NO_DEFAULT_ROLE);
  const [allowedDomains, setAllowedDomains] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  // Ảnh chụp cấu hình vừa tải/lưu từ server — dùng làm mốc so sánh để tính isDirty.
  const [loadedSnapshot, setLoadedSnapshot] = useState<FormSnapshot | null>(null);

  const applySettings = (s: AuthSettings) => {
    const nextGoogleEnabled = s.google_enabled !== 0;
    const nextClientId = s.google_client_id ?? '';
    const nextAutoCreateUser = s.auto_create_user !== 0;
    const nextRequireApproval = s.require_approval !== 0;
    const nextDefaultRoleId = s.default_role_id ?? NO_DEFAULT_ROLE;
    const nextAllowedDomains = s.allowed_domains ?? '';
    const nextWebhookUrl = s.chat_webhook_url ?? '';
    setGoogleEnabled(nextGoogleEnabled);
    setClientId(nextClientId);
    setAutoCreateUser(nextAutoCreateUser);
    setRequireApproval(nextRequireApproval);
    setDefaultRoleId(nextDefaultRoleId);
    setAllowedDomains(nextAllowedDomains);
    setWebhookUrl(nextWebhookUrl);
    setLoadedSnapshot({
      googleEnabled: nextGoogleEnabled,
      clientId: nextClientId,
      autoCreateUser: nextAutoCreateUser,
      requireApproval: nextRequireApproval,
      defaultRoleId: nextDefaultRoleId,
      allowedDomains: nextAllowedDomains,
      webhookUrl: nextWebhookUrl,
    });
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [authSettings, rolePage] = await Promise.all([
        authSettingsApi.get(),
        rolesApi.list({ limit: 200 }),
      ]);
      applySettings(authSettings);
      setRoles(rolePage.items);
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể tải cấu hình đăng nhập Google'), 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Bật đăng nhập Google mà chưa có Client ID — chặn ngay trên UI, không đợi lỗi 400 từ server.
  const missingClientId = googleEnabled && clientId.trim() === '';
  // Bật đăng nhập + tự động tạo tài khoản + KHÔNG yêu cầu duyệt = ai có tài khoản Google
  // trong domain cho phép cũng vào thẳng hệ thống mà không ai kiểm soát.
  const showApprovalWarning = googleEnabled && autoCreateUser && !requireApproval;

  // Có bất kỳ trường nào trên form khác với cấu hình đã tải/lưu gần nhất không.
  // Dùng để chặn "Gửi thử": nút này không được phép âm thầm lưu các thay đổi
  // (kể cả những trường không liên quan tới webhook) chưa được Admin xác nhận.
  const isDirty = loadedSnapshot
    ? googleEnabled !== loadedSnapshot.googleEnabled ||
      clientId !== loadedSnapshot.clientId ||
      autoCreateUser !== loadedSnapshot.autoCreateUser ||
      requireApproval !== loadedSnapshot.requireApproval ||
      defaultRoleId !== loadedSnapshot.defaultRoleId ||
      allowedDomains !== loadedSnapshot.allowedDomains ||
      webhookUrl !== loadedSnapshot.webhookUrl
    : false;
  const webhookConfigured = webhookUrl.trim() !== '';
  const testWebhookDisabled = testing || saving || isDirty || !webhookConfigured;
  const testWebhookDisabledReason = !webhookConfigured
    ? 'Chưa cấu hình webhook Google Chat.'
    : isDirty
      ? 'Có thay đổi chưa lưu trên form — hãy lưu cấu hình trước khi gửi thử.'
      : '';

  // Các cờ gửi lên PHẢI là boolean (đọc về là 0/1, nhưng ghi lên là boolean —
  // xem AuthSettingsUpdate). Gửi số sẽ bị server bỏ qua im lặng.
  const buildPayload = (): AuthSettingsUpdate => ({
    google_enabled: googleEnabled,
    google_client_id: clientId.trim(),
    auto_create_user: autoCreateUser,
    require_approval: requireApproval,
    default_role_id: defaultRoleId === NO_DEFAULT_ROLE ? null : defaultRoleId,
    allowed_domains: allowedDomains.trim(),
    // Nếu Admin không sửa ô webhook, gửi lại nguyên chuỗi (đã che hoặc rỗng) —
    // backend nhận ra chuỗi che trùng khớp và giữ nguyên URL thật đang lưu.
    chat_webhook_url: webhookUrl,
  });

  const handleSave = async (): Promise<boolean> => {
    if (missingClientId) {
      notify('Cần nhập Google Client ID trước khi bật đăng nhập Google', 'error');
      return false;
    }
    setSaving(true);
    try {
      const updated = await authSettingsApi.update(buildPayload());
      applySettings(updated);
      notify('Đã lưu cấu hình đăng nhập Google', 'success');
      return true;
    } catch (err) {
      notify(getApiErrorMessage(err, 'Không thể lưu cấu hình đăng nhập Google'), 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleTestWebhook = async () => {
    // Endpoint test dùng URL ĐÃ LƯU trong DB, không dùng giá trị đang gõ trên form.
    // Nút này chỉ gọi API test — KHÔNG được tự lưu form (nút bị disabled khi form
    // đang dirty, xem testWebhookDisabled/isDirty ở trên).
    setTesting(true);
    try {
      await authSettingsApi.testWebhook();
      notify('Đã gửi thử tin nhắn tới Google Chat', 'success');
    } catch (err) {
      notify(getApiErrorMessage(err, 'Gửi thử webhook thất bại'), 'error');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Đăng nhập Google
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cấu hình đăng nhập bằng tài khoản Google và thông báo qua Google Chat.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Tải lại">
            <IconButton onClick={() => void load()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" disabled={saving} onClick={() => void handleSave()}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Lưu thay đổi'}
          </Button>
        </Stack>
      </Stack>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={googleEnabled}
                  onChange={(e) => setGoogleEnabled(e.target.checked)}
                />
              }
              label="Bật đăng nhập bằng Google"
            />
            {missingClientId && (
              <Alert severity="error" sx={{ mt: 1 }}>
                Cần nhập Google Client ID trước khi bật đăng nhập Google.
              </Alert>
            )}
          </Box>

          <TextField
            label="Google Client ID"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            fullWidth
            helperText="Dán Client ID lấy từ Google Cloud Console (OAuth 2.0)."
          />

          <Divider />

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={autoCreateUser}
                  onChange={(e) => setAutoCreateUser(e.target.checked)}
                />
              }
              label="Tự động tạo tài khoản mới"
            />
            <Typography variant="body2" color="text.secondary">
              Tắt: chỉ những tài khoản đã tạo sẵn mới đăng nhập được bằng Google.
            </Typography>
          </Box>

          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={requireApproval}
                  onChange={(e) => setRequireApproval(e.target.checked)}
                />
              }
              label="Yêu cầu Admin duyệt tài khoản mới"
            />
            <Typography variant="body2" color="text.secondary">
              Bật: tài khoản mới tạo qua Google sẽ ở trạng thái chờ Admin duyệt trước khi dùng được.
            </Typography>
            {showApprovalWarning && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Đang tắt duyệt trong khi vẫn tự động tạo tài khoản mới: bất kỳ ai có tài khoản Google
                thuộc miền được phép sẽ vào thẳng hệ thống mà không qua kiểm duyệt. Hãy cân nhắc kỹ
                trước khi lưu cấu hình này.
              </Alert>
            )}
          </Box>

          <FormControl fullWidth>
            <InputLabel id="google-auth-default-role-label">Vai trò mặc định</InputLabel>
            <Select
              labelId="google-auth-default-role-label"
              label="Vai trò mặc định"
              value={defaultRoleId}
              onChange={(e) => setDefaultRoleId(e.target.value)}
            >
              <MenuItem value={NO_DEFAULT_ROLE}>— Không gán —</MenuItem>
              {roles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Miền email được phép"
            value={allowedDomains}
            onChange={(e) => setAllowedDomains(e.target.value)}
            fullWidth
            helperText="Ngăn cách bằng dấu phẩy, vd: example.com,khac.com. Để trống = cho phép mọi domain."
          />

          <Divider />

          <Stack direction="row" spacing={2} alignItems="flex-start">
            <TextField
              label="Webhook Google Chat"
              value={webhookUrl}
              onChange={(e) => {
                setWebhookUrl(e.target.value);
              }}
              fullWidth
              helperText={
                isDirty
                  ? 'Có thay đổi chưa lưu — hãy lưu cấu hình trước khi gửi thử.'
                  : 'Để trống = tắt thông báo qua Google Chat.'
              }
            />
            <Tooltip title={testWebhookDisabledReason} disableHoverListener={!testWebhookDisabledReason}>
              <span>
                <Button
                  variant="outlined"
                  disabled={testWebhookDisabled}
                  onClick={() => void handleTestWebhook()}
                  sx={{ mt: 1, whiteSpace: 'nowrap' }}
                >
                  {testing ? <CircularProgress size={20} /> : 'Gửi thử'}
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
